import React from 'react'
import { Link } from 'react-router-dom'
import { ToolkitPageLayout } from '../../../components'

interface Breadcrumb {
  label: string
  path: string
}

interface ResourceLink {
  href: string
  imgSrc: string
  altText: string
  caption: string
  isYouTube?: boolean
  youTubeId?: string
}

export const ScdAwarenessStigmaPage: React.FC = () => {
  const pageTitle = 'Lack of Awareness of SCD or Stigma Towards SCD'
  const breadcrumbs: Breadcrumb[] = [
    { label: 'Toolkit', path: '/toolkit' },
    { label: 'Community', path: '/toolkit/community' },
    {
      label: pageTitle,
      path: '/toolkit/community/scd-awareness-stigma'
    }
  ]

  const articleLinks: ResourceLink[] = [
    {
      href: 'https://www.acpjournals.org/doi/10.7326/M20-7418?url_ver=Z39.88-2003&rfr_id=ori:rid:crossref.org&rfr_dat=cr_pub%20%200pubmed',
      imgSrc:
        'https://www.dropbox.com/scl/fi/ei451q5zddsre6hexzntq/Screen-Shot-2021-11-07-at-7.53.31-PM-1024x973.png?rlkey=i777kwdvy6w8dcs9u1jk10lsg&dl=0&dl=1',
      altText: 'Preview of Annals article',
      caption: 'Annals: Kneeling Was the First Step for Sickle Cell Disease'
    },
    {
      href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4627860/',
      imgSrc:
        'https://www.dropbox.com/scl/fi/khq10c9ah5kduxfl6yjkw/Screen-Shot-2021-11-07-at-7.55.45-PM-870x1024.png?rlkey=qy8gltl0t4w0myci0gm050qfn&dl=0&dl=1',
      altText: 'Preview of HHS article on Sickle Cell Stigma',
      caption: 'HHS: The Measure of Sickle Cell Stigma'
    }
  ]

  const videoResources: ResourceLink[] = [
    {
      href: 'https://www.youtube.com/watch?v=5S3JxE_1UOw',
      imgSrc: '', // Not used for direct embed
      altText: 'Suffering in Silence Video',
      caption:
        'Suffering in Silence: 100 Years of Sickle Cell Disease in the U S October 27, 2010 (edited)',
      isYouTube: true,
      youTubeId: '5S3JxE_1UOw'
    },
    {
      href: 'https://www.youtube.com/watch?v=FuelQDBOxXI',
      imgSrc: '', // Not used for direct embed
      altText: 'CRISIS Experiences Video',
      caption:
        'CRISIS: Experiences of People with Sickle Cell Disease Seeking Health Care for Pain',
      isYouTube: true,
      youTubeId: 'FuelQDBOxXI'
    }
  ]

  const supplementalDoc: ResourceLink = {
    href: 'https://docs.google.com/document/d/e/2PACX-1vTByDopCOUwKF2ZVxMSn1lLCAgDri7Smd4FR8UR5GtoWZndPpNl70PZsc105EAFHqa82qsFprpj2RNe/pub',
    imgSrc:
      'https://www.dropbox.com/scl/fi/9xi613hl7xnf5mudakyjv/Screen-Shot-2021-12-23-at-10.46.04-AM-1024x308.png?rlkey=37gp2qx7kn13cim4vvsnosxuh&dl=0&dl=1',
    altText: 'Preview of Systematic Review document',
    caption:
      'See attached pdf with article "A Systematic Review of Barriers and Interventions to Improve Appropriate Use of Therapies for Sickle Cell Disease"'
  }

  const renderResourceLink = (resource: ResourceLink, index: number) => (
    <figure
      key={`article-${index}`}
      className="mb-8 p-4 border rounded-lg shadow-md bg-gray-50 max-w-xl mx-auto"
    >
      <a
        href={resource.href}
        target="_blank"
        rel="noopener noreferrer"
        className="block hover:opacity-80 transition-opacity"
      >
        <img
          src={resource.imgSrc}
          alt={resource.altText}
          className="w-full h-auto rounded shadow-sm mb-2"
        />
        <figcaption className="text-sm text-center text-gray-700">
          {resource.caption}
        </figcaption>
      </a>
    </figure>
  )

  const renderYouTubeVideo = (video: ResourceLink, index: number) => (
    <div key={`video-${index}`} className="mb-10">
      <div className="aspect-w-16 aspect-h-9 h-[50vh] w-[50vw] mx-auto rounded-lg overflow-hidden shadow-lg max-w-2xl mx-auto">
        <iframe
          src={`https://www.youtube.com/embed/${video.youTubeId}`}
          title={video.altText}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
      <p className="mt-2 text-sm text-center text-gray-600">{video.caption}</p>
    </div>
  )

  return (
    <ToolkitPageLayout title={pageTitle} breadcrumbs={breadcrumbs}>
      <div className="prose max-w-none text-[#3C2416]">
        <h3 className="text-xl font-semibold text-[#142841] mb-4">
          1. Review these articles below:
        </h3>
        {articleLinks.map(renderResourceLink)}

        <h3 className="text-xl font-semibold text-[#142841] mt-10 mb-4">
          2. Review video below:
        </h3>
        {renderYouTubeVideo(videoResources[0], 0)}

        <h3 className="text-xl font-semibold text-[#142841] mt-10 mb-4">
          3. Review supplemental materials for the above video below:
        </h3>
        <div className="space-y-10">
          {renderYouTubeVideo(videoResources[1], 1)}
          {renderResourceLink(supplementalDoc, 0)}
        </div>
      </div>

      {/* All Done Button */}
      <div className="mt-16 mb-8 text-center">
        <Link
          to="/toolkit/community" // Link back to Community Section Page
          className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-8 py-3 text-lg font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl"
        >
          All done? Click here to return to Community Resources
        </Link>
      </div>
    </ToolkitPageLayout>
  )
}
